// Accepts alphanumeric CNPJs: first 12 chars are [A-Z0-9], last 2 are always numeric check digits
const CNPJ_REGEX = /^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-\d{2}$/i
const TABLE_TO_CALCULATE_FIRST_DIGIT = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
const TABLE_TO_CALCULATE_SECOND_DIGIT = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

function charToValue(char: string): number {
  return char.toUpperCase().charCodeAt(0) - 48
}

function calculateDigit(chars: string[], table: number[]) {
  const sum = chars.reduce(
    (previousValue, currentValue, index) => previousValue + charToValue(currentValue) * table[index],
    0
  )
  const quotient = sum % 11
  return quotient < 2 ? 0 : 11 - quotient
}

/**
 * Validates a CNPJ (Brazilian company identification number).
 * Supports both numeric (legacy) and alphanumeric (new format from July 2026) CNPJs.
 *
 * @param cnpj - The CNPJ string to validate. It can be either masked (e.g., "12.345.678/0001-95"
 *                        or "A1.2B3.C45/D6E7-90") or unmasked (e.g., "12345678000195").
 * @param withMaskValidation - Whether to validate the CNPJ format with a mask. (Default: `false`)
 *                                               If true, the input must match the masked format.
 * @returns `true` if the CNPJ is valid, `false` otherwise.
 *
 * @example
 * isValidCnpj("12.345.678/0001-95"); // true
 * isValidCnpj("12345678000195"); // true
 * isValidCnpj("12345678000195", true); // false (invalid without mask)
 */
export function isValidCnpj(cnpj: string, withMaskValidation = false) {
  const unmaskedCnpj = cnpj.replace(/[^A-Za-z0-9]/g, '').toUpperCase()

  if (withMaskValidation && !CNPJ_REGEX.test(cnpj.toUpperCase())) {
    return false
  }

  if (unmaskedCnpj.length !== 14) {
    return false
  }

  if (/^(.)\1{11}/.test(unmaskedCnpj.slice(0, 12))) {
    return false
  }

  const lastTwoDigits = unmaskedCnpj.slice(-2)
  const body = unmaskedCnpj.slice(0, 12).split('')

  const thirteenDigit = calculateDigit(body, TABLE_TO_CALCULATE_FIRST_DIGIT)

  if (Number(lastTwoDigits[0]) !== thirteenDigit) {
    return false
  }

  const firstThirteenChars = [...body, thirteenDigit.toString()]
  const fourteenDigit = calculateDigit(firstThirteenChars, TABLE_TO_CALCULATE_SECOND_DIGIT)

  if (Number(lastTwoDigits[1]) !== fourteenDigit) {
    return false
  }

  return true
}
